/* study.svelte.ts generated by Svelte v5.57.2 */
import * as $ from 'svelte/internal/client';
import { Array as Arr, Option } from "effect";
import { grade } from "../core/model/grade.ts";
import { Answer, ReviewId } from "../core/model/types.ts";

import {
	advance,
	answer,
	canAdvance,
	canResume,
	conflicted,
	endEarly,
	extend,
	failed,
	hasNext,
	locked,
	resume,
	retrying,
	saved,
	startSession
} from "../ui/session.ts";

import { noRouter } from "./route.ts";
import { conflictCode } from "./rpc.ts";

const TICK_MS = 5000;

export class StudyController {
	#sets = $.tag($.state(null), 'StudyController.sets');

	get sets() {
		return $.get(this.#sets);
	}

	set sets(value) {
		$.set(this.#sets, value);
	}

	#session = $.tag($.state(null), 'StudyController.session');

	get session() {
		return $.get(this.#session);
	}

	set session(value) {
		$.set(this.#session, value);
	}

	#last = $.tag($.state(null), 'StudyController.last');

	get last() {
		return $.get(this.#last);
	}

	set last(value) {
		$.set(this.#last, value, true);
	}

	#now = $.tag($.state(0), 'StudyController.now');

	get now() {
		return $.get(this.#now);
	}

	set now(value) {
		$.set(this.#now, value, true);
	}

	#failure = $.tag($.state(null), 'StudyController.failure');

	get failure() {
		return $.get(this.#failure);
	}

	set failure(value) {
		$.set(this.#failure, value);
	}

	#busy = $.tag($.state(false), 'StudyController.busy');

	get busy() {
		return $.get(this.#busy);
	}

	set busy(value) {
		$.set(this.#busy, value, true);
	}

	#settingsBusy = $.tag($.state(false), 'StudyController.settingsBusy');

	get settingsBusy() {
		return $.get(this.#settingsBusy);
	}

	set settingsBusy(value) {
		$.set(this.#settingsBusy, value, true);
	}

	#settingsError = $.tag($.state(null), 'StudyController.settingsError');

	get settingsError() {
		return $.get(this.#settingsError);
	}

	set settingsError(value) {
		$.set(this.#settingsError, value);
	}

	#ticket = 0;
	#timer = null;
	#unlisten = null;
	#source;
	#clock;
	#newId;
	#router;

	constructor(
		source,
		clock = Date.now,
		newId = () => crypto.randomUUID(),
		router = noRouter
	) {
		this.#source = source;
		this.#clock = clock;
		this.#newId = newId;
		this.#router = router;
		this.now = clock();
	}

	dispose() {
		this.#ticket++;
		this.#unlisten?.();
		this.#unlisten = null;

		if ($.strict_equals(this.#timer, null, false)) clearInterval(this.#timer);

		this.#timer = null;
	}

	#set(session) {
		this.session = session;
		this.now = this.#clock();

		if (session && $.strict_equals(this.#timer, null)) this.#timer = setInterval(() => this.now = this.#clock(), TICK_MS);

		if (!session && $.strict_equals(this.#timer, null, false)) {
			clearInterval(this.#timer);
			this.#timer = null;
		}
	}

	async #attempt(title, run, back, backLabel) {
		const mine = ++this.#ticket;

		this.busy = true;

		try {
			const apply = (await $.track_reactivity_loss(run()))();

			if ($.strict_equals(mine, this.#ticket, false)) return;

			this.failure = null;
			apply();
		} catch(error) {
			if ($.strict_equals(mine, this.#ticket, false)) return;

			this.failure = {
				title,
				error,
				at: this.#clock(),
				retry: () => this.#attempt(title, run, back, backLabel),
				back,
				backLabel
			};
		} finally {
			if ($.strict_equals(mine, this.#ticket)) this.busy = false;
		}
	}

	#fetchSets(follow) {
		return this.#attempt("Sets did not load", async () => {
			const at = this.#clock();
			const next = (await $.track_reactivity_loss(this.#source.sets(at)))();
			const slug = follow ? this.#router.current() : null;

			const target = $.strict_equals(slug, null)
				? undefined
				: next.find((s) => $.strict_equals(s.slug, slug));

			const reply = target
				? (await $.track_reactivity_loss(this.#source.queue(target.id, this.#clock())))()
				: null;

			return () => {
				this.now = at;
				this.sets = next;

				if (target && reply && $.strict_equals(this.#router.current(), slug)) {
					this.last = target.id;
					this.#route(target.id, true);

					return this.#applyQueue(target.id, reply);
				}

				if (follow) this.#follow(this.#router.current());
			};
		});
	}

	load = () => this.#fetchSets(false);

	open = () => {
		this.#unlisten ??= this.#router.listen((slug) => this.#follow(slug));

		return this.#fetchSets(true);
	};

	#route(setId, replace) {
		const set = $.strict_equals(setId, null)
			? undefined
			: this.sets?.find((s) => $.strict_equals(s.id, setId));

		this.#router.go(set?.slug ?? null, set?.name ?? null, replace);
	}

	#follow(slug) {
		if ($.strict_equals(this.sets, null)) return;

		const s = this.session;

		if (s && locked(s)) return this.#route(s.setId, false);

		const target = $.strict_equals(slug, null)
			? undefined
			: this.sets.find((set) => $.strict_equals(set.slug, slug));

		if (target) {
			if ($.strict_equals(s?.setId, target.id, false)) this.#begin(target.id);

			return;
		}

		if ($.strict_equals(slug, null, false)) this.#route(null, true);
		if (s || this.failure) this.#leave();
	}

	pauseHanziPrompts = async (paused) => {
		if (this.session || this.busy || this.settingsBusy) return;

		this.settingsBusy = true;
		this.settingsError = null;

		try {
			const at = this.#clock();
			const next = (await $.track_reactivity_loss(this.#source.pauseHanziPrompts(paused, at)))();

			this.now = at;
			this.sets = next;
		} catch(error) {
			this.settingsError = error;
		} finally {
			this.settingsBusy = false;
		}
	};

	start = (setId) => {
		if (this.busy || this.settingsBusy) return;

		this.#begin(setId);
	};

	#begin(setId) {
		this.last = setId;
		this.#route(setId, false);

		this.#attempt(
			"This session did not load",
			async () => {
				const reply = (await $.track_reactivity_loss(this.#source.queue(setId, this.#clock())))();

				return () => this.#applyQueue(setId, reply);
			},
			() => {
				this.#clearFailure();
				this.#route(null, false);
			}
		);
	}

	#applyQueue(setId, reply) {
		if (Arr.isReadonlyArrayNonEmpty(reply.cards)) return this.#set(startSession(setId, reply.cards, reply.set));

		this.#route(null, true);
		this.load();
	}

	#clearFailure = () => {
		this.#ticket++;
		this.busy = false;
		this.failure = null;
	};

	#more(setId) {
		this.#attempt(
			"More cards did not load",
			async () => {
				const reply = (await $.track_reactivity_loss(this.#source.queue(setId, this.#clock())))();

				return () => this.session && this.#set(extend(this.session, reply, this.#clock()));
			},
			() => {
				this.#clearFailure();

				if (this.session) this.#set(endEarly(this.session));
			},
			"End session"
		);
	}

	#reload(setId) {
		this.#attempt(
			"The session did not reload",
			async () => {
				const reply = (await $.track_reactivity_loss(this.#source.queue(setId, this.#clock())))();

				return () => this.session && this.#set(extend(this.session, reply, this.#clock(), true));
			},
			this.#leave
		);
	}

	async #save(cardId, request) {
		try {
			const reply = (await $.track_reactivity_loss(this.#source.review(cardId, request, this.#clock())))();

			if (this.session) this.#set(saved(this.session, reply));
		} catch(error) {
			const s = this.session;

			if (!s) return;

			const code = conflictCode(error);

			if ($.strict_equals(code, null)) return this.#set(failed(s, request.reviewId, error));

			const next = conflicted(s, request.reviewId, code, error);

			this.#set(next);

			if ($.strict_equals(code, "stale_content") && $.strict_equals(next, s, false)) this.#reload(s.setId);
		}
	}

	submit = (given) => {
		const s = this.session;
		const t = s?.turn;

		if (!s || s.ended || $.strict_equals(t?.tag, "asking", false)) return;

		const text = Answer.makeOption(given.trim().normalize("NFC"));

		if (Option.isNone(text)) return;

		const request = {
			reviewId: ReviewId.make(this.#newId()),
			reps: t.card.reps,
			contentRev: t.card.contentRev,
			settingsRev: t.card.settingsRev,
			given: text.value
		};

		this.#set(answer(s, given, grade(t.card, given), request));
		this.#save(t.card.id, request);
	};

	retry = () => {
		const s = this.session;
		const t = s?.turn;

		if (!s || $.strict_equals(t?.tag, "answered", false) || $.strict_equals(t.save.tag, "failed", false)) return;

		this.#set(retrying(s));
		this.#save(t.card.id, t.save.request);
	};

	next = () => {
		const s = this.session;

		if (!s || this.busy || !canAdvance(s)) return;

		const now = this.#clock();

		if (hasNext(s, now)) this.#set(advance(s, now)); else this.#more(s.setId);
	};

	resume = () => {
		const s = this.session;
		const now = this.#clock();

		if (s && !this.busy && canResume(s, now)) this.#set(resume(s, now));
	};

	end = () => {
		const s = this.session;

		if (!s || this.busy || locked(s)) return;
		if (!s.log.length) this.#leave(); else this.#set(endEarly(s));
	};

	back = () => {
		const s = this.session;

		if (s && locked(s)) return;

		this.#leave();
	};

	#leave = () => {
		this.#clearFailure();
		this.#set(null);
		this.#route(null, false);
		this.load();
	};
}